import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { useDialogActions } from "~/store/dialogStore";
import { UnsavedChangesDialog } from "~/components/UnsavedChangesDialog";
import { AlertTriangle } from "lucide-react";

interface UseUnsavedChangesWarningProps {
  hasUnsavedChanges: boolean;
  onConfirmNavigation?: () => void;
}

export function useUnsavedChangesWarning({
  hasUnsavedChanges,
  onConfirmNavigation,
}: UseUnsavedChangesWarningProps) {
  const navigate = useNavigate();
  const { openDialog, closeDialog, setPendingConfirmAction } = useDialogActions();

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const showUnsavedChangesDialog = useCallback((action?: () => void) => {
    const confirmAction = () => {
      onConfirmNavigation?.();
      action?.();
    };

    setPendingConfirmAction(confirmAction);

    openDialog({
      title: "Alterações não salvas",
      icon: <AlertTriangle />,
      content: <UnsavedChangesDialog onCancel={closeDialog} />,
    });
  }, [openDialog, closeDialog, setPendingConfirmAction, onConfirmNavigation]);

  const checkUnsavedChanges = useCallback((targetPath: string) => {
    if (hasUnsavedChanges) {
      showUnsavedChangesDialog(() => {
        navigate(targetPath);
      });
      return true;
    }
    return false;
  }, [hasUnsavedChanges, showUnsavedChangesDialog, navigate]);

  return {
    checkUnsavedChanges,
    showUnsavedChangesDialog,
  };
}
